import { RouterContext } from '@optools/routes/__root';
import { EnvironmentConfig } from '@optools/services/config/types';
import { redirect } from '@tanstack/react-router';
import { kebabCase } from 'es-toolkit';
import { get } from 'es-toolkit/compat';
import type { Paths } from 'type-fest';

import { isAuthorized } from './index';

type ToolKey = keyof EnvironmentConfig['tools'];
type ToolAuth = EnvironmentConfig['tools'][ToolKey]['auth'];

type StripAuthData<T> =
  T extends Record<string, unknown>
    ? { [K in Exclude<keyof T, 'groups' | 'featureGroups'>]: StripAuthData<T[K]> }
    : T;

type PagePaths<T extends ToolKey> = EnvironmentConfig['tools'][T]['auth'] extends { pages: infer P }
  ? Paths<StripAuthData<P>>
  : never;

export type AuthPath =
  | ToolKey
  | { [T in ToolKey]: PagePaths<T> extends infer S extends string ? `${T}.${S}` : never }[ToolKey];

const resolveAuthGroups = (auth: ToolAuth, segments: string[]): string[] => {
  if (!segments.length) return auth.groups;

  const groups = get(auth, ['pages', ...segments, 'groups']);
  return Array.isArray(groups) ? (groups as string[]) : auth.groups;
};

export type AuthorizedRouteLoaderOptions = {
  /**
   * Redirect target when the group check fails.
   * Default: nested path → tool root (e.g. `/btc-tools`); tool-level only → `/unauthorized`.
   */
  unauthorizedRedirect?: string | ((args: { location: { pathname: string } }) => string);
};

export function authorizedRouteLoader<const P extends AuthPath>(
  path: P,
  options?: AuthorizedRouteLoaderOptions,
): (args: { context: RouterContext; location: { pathname: string } }) => Promise<undefined>;
export function authorizedRouteLoader(path: string, options?: AuthorizedRouteLoaderOptions) {
  return async ({
    context,
    location,
  }: {
    context: RouterContext;
    location: { pathname: string };
  }): Promise<undefined> => {
    const {
      auth: { oktaAuth },
      config,
      profile,
    } = context;

    if (!(await oktaAuth.isAuthenticated())) return;

    const [tool, ...pageSegments] = path.split('.') as [ToolKey, ...string[]];
    const toolConfig = config.tools[tool];

    if ('enabled' in toolConfig && !toolConfig.enabled) {
      throw redirect({ to: '/' });
    }

    const allowedGroups = resolveAuthGroups(toolConfig.auth, pageSegments);

    if (!isAuthorized(allowedGroups, profile.groups)) {
      const fallback = pageSegments.length ? `/${kebabCase(tool)}` : '/unauthorized';
      const to =
        typeof options?.unauthorizedRedirect === 'function'
          ? options.unauthorizedRedirect({ location })
          : (options?.unauthorizedRedirect ?? fallback);
      throw redirect({ to });
    }
  };
}
